import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Euro, Wallet, PiggyBank,
  ArrowUpRight, ArrowDownRight, Sparkles, RefreshCw,
  Calendar, Filter, Download, Users, Target, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BusinessFinanceDashboard = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // 'week', 'month', 'year'
  const [financeData, setFinanceData] = useState(null);
  const [reactivationROI, setReactivationROI] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchFinanceData();
    }
  }, [user?.id, period]);

  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      // Fetch multiple data sources
      const [statsRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/stats/business/${user.id}`),
        axios.get(`${API}/reactivation/campaigns/business/${user.id}`).catch(() => ({ data: { campaigns: [] } }))
      ]);

      // Calculate reactivation benefits
      const campaigns = campaignsRes.data.campaigns || [];
      let totalReactivationRevenue = 0;
      let totalManagementCost = 0;
      let totalCommissionPaid = 0;
      let customersReactivated = 0;

      campaigns.forEach(campaign => {
        if (campaign.status === 'completed' || campaign.status === 'active') {
          const revenue = campaign.total_revenue_generated || 0;
          const managementFee = campaign.management_fee || 0;
          const commission = (revenue * (campaign.commission_percentage || 30)) / 100;
          
          totalReactivationRevenue += revenue;
          totalManagementCost += managementFee;
          totalCommissionPaid += commission;
          customersReactivated += campaign.customers_converted || 0;
        }
      });

      const netReactivationBenefit = totalReactivationRevenue - totalManagementCost - totalCommissionPaid;

      setFinanceData({
        totalSales: statsRes.data.totalSales || 0,
        totalOrders: statsRes.data.totalOrders || 0,
        activeKupons: statsRes.data.activeKupons || 0,
        avgOrderValue: statsRes.data.totalOrders > 0 
          ? (statsRes.data.totalSales / statsRes.data.totalOrders).toFixed(2)
          : 0
      });

      setReactivationROI({
        totalRevenue: totalReactivationRevenue,
        managementCost: totalManagementCost,
        commissionPaid: totalCommissionPaid,
        netBenefit: netReactivationBenefit,
        customersReactivated,
        campaignCount: campaigns.length,
        roi: totalManagementCost > 0 
          ? ((netReactivationBenefit / totalManagementCost) * 100).toFixed(0)
          : 0
      });

    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-local-sage" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-manrope font-bold text-2xl flex items-center gap-2">
            <Wallet className="w-7 h-7 text-local-sage" />
            Dashboard Financiero
          </h2>
          <p className="text-muted-foreground mt-1">
            Resumen de ingresos, gastos e inversiones en reactivación
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {['week', 'month', 'year'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  period === p 
                    ? 'bg-white dark:bg-gray-700 shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchFinanceData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Euro className="w-6 h-6" />
            </div>
            <ArrowUpRight className="w-5 h-5 text-emerald-200" />
          </div>
          <p className="text-emerald-100 text-sm">Ventas Totales</p>
          <p className="text-3xl font-bold mt-1">{formatCurrency(financeData?.totalSales || 0)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-muted-foreground text-sm">Pedidos</p>
          <p className="text-3xl font-bold mt-1">{financeData?.totalOrders || 0}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <p className="text-muted-foreground text-sm">Ticket Medio</p>
          <p className="text-3xl font-bold mt-1">{formatCurrency(financeData?.avgOrderValue || 0)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <p className="text-muted-foreground text-sm">Kupones Activos</p>
          <p className="text-3xl font-bold mt-1">{financeData?.activeKupons || 0}</p>
        </motion.div>
      </div>

      {/* Reactivation ROI Section - THE KEY FEATURE */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-br from-local-sage/10 to-emerald-50 dark:from-local-sage/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-local-sage/20"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-local-sage flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Inversión en Reactivación</h3>
            <p className="text-sm text-muted-foreground">
              Beneficios generados de clientes que habías perdido
            </p>
          </div>
        </div>

        {reactivationROI?.campaignCount > 0 ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              {/* Revenue Generated */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
                <p className="text-sm text-muted-foreground mb-1">Ingresos Recuperados</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(reactivationROI.totalRevenue)}
                </p>
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                  <ArrowUpRight className="w-3 h-3" />
                  De clientes perdidos
                </p>
              </div>

              {/* Management Cost */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                <p className="text-sm text-muted-foreground mb-1">Inversión en Gestión</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(reactivationROI.managementCost)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Coste de campaña
                </p>
              </div>

              {/* Commission */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                <p className="text-sm text-muted-foreground mb-1">Comisión LokAlGrow</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(reactivationROI.commissionPaid)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Sobre ventas generadas
                </p>
              </div>

              {/* Net Benefit - HIGHLIGHTED */}
              <div className="bg-emerald-500 text-white rounded-xl p-4 col-span-2 lg:col-span-1">
                <p className="text-sm text-emerald-100 mb-1">BENEFICIO NETO</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(reactivationROI.netBenefit)}
                </p>
                <p className="text-xs text-emerald-100 flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3" />
                  Tu ganancia real
                </p>
              </div>

              {/* ROI */}
              <div className="bg-local-sage text-white rounded-xl p-4">
                <p className="text-sm text-white/80 mb-1">ROI</p>
                <p className="text-2xl font-bold">
                  {reactivationROI.roi}%
                </p>
                <p className="text-xs text-white/80 mt-1">
                  Retorno de inversión
                </p>
              </div>
            </div>

            {/* Summary Message */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-border">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">
                    Has recuperado <span className="text-emerald-600 font-bold">{reactivationROI.customersReactivated} clientes</span> que 
                    habías perdido
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Estos clientes habrían generado <strong>0€</strong> sin la reactivación. 
                    Ahora has ganado <span className="text-emerald-600 font-semibold">{formatCurrency(reactivationROI.netBenefit)}</span> netos 
                    gracias a invertir en recuperarlos.
                  </p>
                </div>
              </div>
            </div>

            {/* Breakdown Table */}
            <div className="mt-6">
              <h4 className="font-semibold mb-3">Desglose de la Inversión</h4>
              <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="text-left p-3 font-medium">Concepto</th>
                      <th className="text-right p-3 font-medium">Importe</th>
                      <th className="text-right p-3 font-medium">Tipo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          Ingresos de clientes reactivados
                        </div>
                      </td>
                      <td className="p-3 text-right font-semibold text-emerald-600">
                        +{formatCurrency(reactivationROI.totalRevenue)}
                      </td>
                      <td className="p-3 text-right">
                        <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full text-xs">
                          Beneficio
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                          Coste de gestión de campañas
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        -{formatCurrency(reactivationROI.managementCost)}
                      </td>
                      <td className="p-3 text-right">
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs">
                          Inversión
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-purple-500" />
                          Comisión sobre ventas generadas
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        -{formatCurrency(reactivationROI.commissionPaid)}
                      </td>
                      <td className="p-3 text-right">
                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs">
                          Comisión
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-emerald-50 dark:bg-emerald-900/20 font-semibold">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <PiggyBank className="w-4 h-4 text-emerald-600" />
                          BENEFICIO NETO TOTAL
                        </div>
                      </td>
                      <td className="p-3 text-right text-emerald-600 text-lg">
                        {formatCurrency(reactivationROI.netBenefit)}
                      </td>
                      <td className="p-3 text-right">
                        <span className="px-2 py-1 bg-emerald-500 text-white rounded-full text-xs">
                          Tu ganancia
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-xl">
            <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="font-semibold mb-2">Aún no tienes campañas de reactivación</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              La reactivación no es un gasto, es una inversión. Recupera clientes que habías perdido 
              y convierte el 0€ que generarían en ingresos reales.
            </p>
            <Button className="mt-4 bg-local-sage hover:bg-local-sage/90">
              <Sparkles className="w-4 h-4 mr-2" />
              Solicitar Demo de Reactivación
            </Button>
          </div>
        )}
      </motion.div>

      {/* Philosophy Note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-300">
              Filosofía de Preeminencia
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              Cada euro invertido en reactivación proviene de clientes que habías olvidado, 
              desechado o perdido la esperanza en recuperar. Sin esta inversión, esos ingresos 
              serían exactamente <strong>0€</strong>. LokAlGrow solo gana cuando tú ganas.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
